import { useCallback,useState } from "react"

import { needsGMVCheck } from "@/modules/helpers/utils/campaign-form"
import type { User } from "@/modules/types"

type ModalState = {
  validateChannel: boolean
  gmvChecking: boolean
  selectSku: boolean
  updateAddress: boolean
}

export function useModalFlow(user: Partial<User> | null | undefined, isNeedGmv?: boolean) {
  const [modals, setModals] = useState<ModalState>({
    validateChannel: false,
    gmvChecking: false,
    selectSku: false,
    updateAddress: false
  })

  const openModal = useCallback((modal: keyof ModalState) => {
    setModals((prev) => ({ ...prev, [modal]: true }))
  }, [])

  const closeModal = useCallback((modal: keyof ModalState) => {
    setModals((prev) => ({ ...prev, [modal]: false }))
  }, [])

  const closeAllModals = useCallback(() => {
    setModals({
      validateChannel: false,
      gmvChecking: false,
      selectSku: false,
      updateAddress: false
    })
  }, [])

  const handleConnectSocialCompleted = useCallback(() => {
    closeModal("validateChannel")
    if (needsGMVCheck(user, isNeedGmv)) {
      openModal("gmvChecking")
    } else {
      openModal("selectSku")
    }
  }, [user, isNeedGmv, closeModal, openModal])

  const handleGMVCheckingCompleted = useCallback(() => {
    closeModal("gmvChecking")
    openModal("selectSku")
  }, [closeModal, openModal])

  const handleUpdateAddress = useCallback(() => {
    closeModal("selectSku")
    setTimeout(() => openModal("updateAddress"), 300)
  }, [closeModal, openModal])

  const handleAddressUpdated = useCallback(() => {
    closeModal("updateAddress")
    setTimeout(() => openModal("selectSku"), 300)
  }, [closeModal, openModal])

  const handleAddressModalClose = useCallback(() => {
    closeModal("updateAddress")
    openModal("selectSku")
  }, [closeModal, openModal])

  return {
    modals,
    openModal,
    closeModal,
    closeAllModals,
    handleConnectSocialCompleted,
    handleGMVCheckingCompleted,
    handleUpdateAddress,
    handleAddressUpdated,
    handleAddressModalClose
  }
}
